import { intl } from "LocaleWrapper";
import { Session } from "ezStore";
import { z } from "zod";
import { ZodSchema } from "@validation/utils/schemaUtils";
import { Fields, hasOptionalFieldPermission, hasPluginPermission } from "imports/Utils";
import { locationSelectOptionSchema } from "view/locations/utils/location.schema";
import { relationSelectOptionSchema } from "view/relations/utils/relation.schema";
import { AvailabilityEnum, BargeType, CharterSubtype, FuelType, TankCladdingType, TankConfiguration, TankPosition, VesselType } from "__generated__/enums";
import { openingHoursSchema } from "src/zod/custom.schema";
import { hasCapacityTeuPermission } from "./ShipUtils";
import { fleetSelectOptionSchema } from "view/fleets/utils/fleet.schema";

export const shipSelectOptionSchema = z.object({
	id: z.number(),
	name: z.string(),
	eni: z.string().nullish(),
	mmsi: z.number().nullish(),
	operator: z.object({ id: z.number(), name: z.string() }).nullish(),
	owner: z.object({ id: z.number(), name: z.string() }).nullish(),
});

export const updateShipNameSchema = z.object({
	name: z.string().min(1),
	operator: z.lazy(() => relationSelectOptionSchema).nullable(),
});

export type UpdateShipNameSchemaInput = z.input<typeof updateShipNameSchema>;
export type UpdateShipNameSchemaOutput = z.output<typeof updateShipNameSchema>;

export const shipSchema = (session: Session) => z.object({
	id: z.number().nullable(),
	externalId: z.string(),
	name: z.string().min(3).max(200),
	vesselType: z.enum(VesselType),
	abbreviation: z.string().max(6),
	isDummy: z.boolean(),
	imo: z.number().nullable(),
	eni: z.string(),
	mmsi: z.number().nullable(),
	licensePlate: z.string(),
	gmp: z.string().max(50),
	niwo: z.string().max(50),
	cemtClassificationType: z.object({ id: z.number(), canonicalName: z.string().nullish() }).nullable(),
	yearOfBuild: z.number().max(9999).nullable(),
	flag: z.string(),
	bargeType: z.enum(BargeType).nullable(),
	classificationSociety: z.string().max(50),
	insurer: z.string().max(100),
	adnProperties: z.object({
		shipType: z.string().nullable(),
		cargoTankDesign: z.string().nullable(),
		cargoTankType: z.string().nullable(),
	}),
	tankCladdingType: z.enum(TankCladdingType).nullable(),
	capacityTon: z.number().max(65535).nullable(),
	capacityM3: z.number().max(65535).nullable(),
	capacityTeu: z.number().max(99999.99).nullable(),
	tracks: z.number().max(65535).nullable(),
	lengthCm: z.number().min(5).max(25000).nullable(),
	widthCm: z.number().min(5).max(4000).nullable(),
	minDraughtCm: z.number().max(99.99).nullable(),
	maxDraughtCm: z.number().max(99.99).nullable(),
	pumpCapacity: z.number().nullable(),
	numberOfTanks: z.number().nullable(),
	fuelType: z.enum(FuelType).nullable(),
	avgVelocityEmptyKmH: z.number().max(999).nullable(),
	avgVelocityFullKmH: z.number().max(999).nullable(),
	avgFuelConsumptionLtrH: z.number().nullable(),
	avgFuelConsumptionLtrKm: z.number().nullable(),
	yearLastMaintenance: z.number().max(9999).nullable(),
	bobbyCat: z.boolean(),
	sailors: z.number().nullable(),
	numReeferConnections: z.number().max(50).nullable(),
	dropline: z.number().max(9999).nullable(),
	trussing: z.string().max(100),
	hasPushBow: z.boolean(),
	hasPushingCertificate: z.boolean(),
	cellGuides: z.boolean(),
	hasShutters: z.boolean(),
	shuttersType: z.string().max(50),
	deckLoad: z.number().max(9999.999).nullable(),
	avgSpeedUpstreamKmH: z.number().max(99999.999).nullable(),
	avgSpeedDownstreamKmH: z.number().max(99999.999).nullable(),
	numberOfCranes: z.number().max(2).nullable(),
	maxCranesOnQuay: z.number().max(3).nullable(),
	trackerId: z.number().max(999999999).nullable(),
	trackTraceProvider: z.string().nullable(),
	maxTonBallast: z.number().max(9999).nullable(),
	airDraft: z.number().max(99.99).nullable(),
	corridor: z.string().max(50),
	tankEye: z.boolean(),
	hasHeatingInstallation: z.boolean(),
	boiler: z.boolean(),
	spillRail: z.boolean(),
	selfPropelled: z.boolean(),
	sailingSchedule: z.number().nullable(),
	memo: z.string().max(65535),
	planningNotes: z.string().max(65535),

	shipManager: relationSelectOptionSchema.nullable(),
	skipper: relationSelectOptionSchema.nullable(),
	shipOwner: relationSelectOptionSchema.nullable(),
	operator: relationSelectOptionSchema.nullable(),
	coupledShip: shipSelectOptionSchema.nullable(),
	defaultCharterSubtype: z.enum(CharterSubtype).nullable(),
	retrieveActuals: z.boolean(),
	showLastProducts: z.boolean(),
	tracking: z.boolean(),
	advancedTracking: z.boolean(),

	bayPlan: z.object({
		rows: z.number(),
		bays: z.number(),
		tiers: z.number(),
		type: z.enum(BargeType),
		properties: z.array(
			z.object({
				tier: z.number(),
			}).catchall(
				z.record(z.string(), z.array(z.number())).nullable(),
			),
		).nullable(),
	}).nullable(),

	tankProperties: z.object({
		tankConfiguration: z.enum(TankConfiguration),
	}),
	tanks: z.array(z.object({
		id: z.number().nullable(),
		position: z.enum(TankPosition),
		positionNumber: z.number(),
		capacityM3: z.number().nullable(),
	})),

	contacts: z.array(z.object({
		id: z.number().nullable(),
		type: z.string(),
		name: z.string(),
		insertion: z.string(),
		surName: z.string(),
		email: z.email().or(z.literal("")).optional(),
		telephone: z.string(),
		fax: z.string(),
		address: z.object({
			name1: z.string(),
			name2: z.string(),
			street: z.string(),
			zipCode: z.string(),
			town: z.string(),
			country: z.string(),
		}),
		rhineLicense: z.boolean(),
		grootVaarbewijsA: z.boolean(),
		grootVaarbewijsB: z.boolean(),
		radarLicense: z.boolean(),
	})),

	availability: z.object({
		availableMonday: z.boolean(),
		availableTuesday: z.boolean(),
		availableWednesday: z.boolean(),
		availableThursday: z.boolean(),
		availableFriday: z.boolean(),
		availableSaturday: z.boolean(),
		availableSunday: z.boolean(),

		availableHoursADay: z.number().nullable(),
		availableDayNight: z.enum(AvailabilityEnum),
		defaultAvailability: z.boolean(),
	}),

	workingHours: openingHoursSchema,

	tonnageCertificates: z.array(z.object({
		depth: z.number(),
		tonnage: z.number().nullable(),
	})),

	agents: z.array(z.object({
		location: ZodSchema.requiredField(locationSelectOptionSchema),
		agent: ZodSchema.requiredField(relationSelectOptionSchema),
	})),
}).superRefine((data, ctx) => {
	if (data.vesselType === VesselType.SeaShip && data.imo === null) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "imo" ],
		});
	}

	if (data.vesselType === VesselType.Barge && data.eni === "") {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "eni" ],
		});
	}

	if (
		data.bargeType !== BargeType.PusherTug &&
		data.capacityTon === null &&
		hasOptionalFieldPermission(session, Fields.SHIP, "capacityTon")
	) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "capacityTon" ],
		});
	}

	if (
		data.bargeType !== BargeType.PusherTug &&
		data.capacityM3 === null &&
		hasOptionalFieldPermission(session, Fields.SHIP, "capacityM3")
	) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "capacityM3" ],
		});
	}

	if (data.capacityTeu === null && hasCapacityTeuPermission(session, data)) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "capacityTeu" ],
		});
	}
});

export type ShipSchemaInput = z.input<ReturnType<typeof shipSchema>>;
export type ShipSchemaOutput = z.output<ReturnType<typeof shipSchema>>;

export const shipAvailabilitySchema = (session: Session) => z.object({
	id: z.number().nullable(),
	allDay: z.boolean(),
	startDate: ZodSchema.requiredField(z.number()),
	endDate: ZodSchema.requiredField(z.number()),
	startMasterdataLocation: locationSelectOptionSchema.nullable(),
	startLocation: z.string(),
	startPosition: z.object({
		latitude: z.number().nullable(),
		longitude: z.number().nullable(),
	}),
	endMasterdataLocation: locationSelectOptionSchema.nullable(),
	endLocation: z.string(),
	endPosition: z.object({
		latitude: z.number().nullable(),
		longitude: z.number().nullable(),
	}),
	remark: z.string().max(255),
}).superRefine((data, ctx) => {
	if (hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations") && !data.startMasterdataLocation) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "startMasterdataLocation" ],
		});
	}

	if (hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations") && !data.endMasterdataLocation) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "endMasterdataLocation" ],
		});
	}
});

export type ShipAvailabilitySchemaInput = z.input<ReturnType<typeof shipAvailabilitySchema>>;
export type ShipAvailabilitySchemaOutput = z.output<ReturnType<typeof shipAvailabilitySchema>>;

export const createShipHoldSchema = z.object({
	rows: ZodSchema.requiredField(z.number().min(1).max(5)),
	bays: ZodSchema.requiredField(z.number().min(1).max(10)),
	tiers: ZodSchema.requiredField(z.number().min(1).max(4)),
});

export type CreateShipHoldSchemaInput = z.input<typeof createShipHoldSchema>;
export type CreateShipHoldSchemaOutput = z.output<typeof createShipHoldSchema>;

export const createBayplanTemplateSchema = z.object({
	name: z.string().min(1).max(255),
	overrideTemplate: z.object({ id: z.number(), name: z.string(), bayPlan: z.string() }).nullable(),
});

export type CreateBayplanTemplateSchemaInput = z.input<typeof createBayplanTemplateSchema>;
export type CreateBayplanTemplateSchemaOutput = z.output<typeof createBayplanTemplateSchema>;

export const shipEventSchema = z.object({
	ships: z.array(shipSelectOptionSchema).nullable(),
	fleets: z.array(fleetSelectOptionSchema).nullable(),
	window: z.tuple([ z.date().nullable(), z.date().nullable() ]),
	satisfyOnContractHours: z.boolean(),
	contractHours: z.array(z.object({
		ship: shipSelectOptionSchema,
		hours: z.number().nullable(),
	})),
});

export type ShipEventSchemaInput = z.input<typeof shipEventSchema>;
export type ShipEventSchemaOutput = z.output<typeof shipEventSchema>;

export const uploadShipsSchema = z.object({
	operator: ZodSchema.requiredField(relationSelectOptionSchema),
	file: z.array(z.file()).min(1),
});

export type UploadShipsSchemaInput = z.input<typeof uploadShipsSchema>;
export type UploadShipsSchemaOutput = z.output<typeof uploadShipsSchema>;
